/**
 * Maps API-aligned entities into print view-models (`types.ts`).
 *
 * Backend reference: `KivuIMSProxyService/src/main/resources/graphql/types.graphqls`
 * — `Order` / `OrderItem` / `OrderItemDetail`, `Invoice`, `Receipt`, `Store`, `Address`,
 * `CompanyBasicInfo` (name via Redux from `getCompanyBasicInfo`), `Customer` (listCustomers).
 *
 * Data loading (reuse existing hooks, do not duplicate fetches here):
 * - Order lines: `useGetOrder` → `components/Orders/types/salesOrder` `Order`
 * - Invoice / receipt: `useGetInvoiceById`, `useGetReceiptById`
 * - Store header: `state.store.storeList` from `useListStores`
 * - Company name: `state.company.companyName` from `useGetCompanyDetails` / bootstrap
 * - Customer bill-to: `state.customers.customerList` from `useGetCustomerList`
 * - Dates / currency display: `formatDate`, `formatDateTime`, `useFormatCurrency` (layouts)
 * - Line labels: `order.items[].variationName` / `detail` (variationName / productName); `usePrintOrderLineDisplayNames` mirrors that; else id.
 */
import type {
  Order,
  OrderItem,
  OrderItemDetail
} from "../Orders/types/salesOrder";
import type { Invoice } from "../Invoice/types/invoice";
import type { Receipt } from "../Receipt/types/receipt";
import type { Customer } from "../../Graphql/types/customer";
import type { GraphqlAddressLike } from "../../Utils/formatAddressLines";

/**
 * Minimal store shape needed for print headers. Structurally compatible with
 * both `Store` (hooks/api/useStore) and `CompanyStore` (components/Store).
 */
export type PrintableStore = {
  id: string;
  name: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  address?: GraphqlAddressLike | null;
};
import { PaymentMethodLabels } from "../Payment/enums/paymentMethod";
import { PaymentMethod } from "../Payment/enums/paymentMethod";
import { formatDate, formatDateTime } from "../Orders/utils/orderUtils";
import { formatAddressToLines } from "../../Utils/formatAddressLines";
import type {
  InvoiceBillTo,
  InvoicePrintLayoutProps,
  InvoicePrintLineItem,
  PrintLineItem,
  ReceiptPrintLayoutProps
} from "./types";

/**
 * Display name for an order line: `variationName`, then `detail.variationName`, then `detail.productName`, then
 * optional `usePrintOrderLineDisplayNames` map, then id fallback.
 */
function lineItemDisplayName(
  it: OrderItem,
  lineDisplayNames?: Record<string, string> | null
): string {
  const detail: OrderItemDetail | null | undefined = it.detail ?? null;
  const variation = it.variationName?.trim() || detail?.variationName?.trim();
  if (variation) return variation;
  const product = detail?.productName?.trim();
  if (product) return product;
  const resolved = lineDisplayNames?.[it.itemId]?.trim();
  if (resolved) return resolved;
  const productItemId = (detail?.productItemId ?? it.itemId)?.trim();
  if (productItemId) return productItemId;
  return "Line item";
}

export function orderItemsToReceiptLines(
  items: OrderItem[],
  lineDisplayNames?: Record<string, string> | null
): PrintLineItem[] {
  return items.map((it) => ({
    name: lineItemDisplayName(it, lineDisplayNames),
    quantity: it.quantity,
    unitPrice: it.unitPrice,
    lineTotal: it.lineTotal
  }));
}

export function orderItemsToInvoiceLines(
  items: OrderItem[],
  lineDisplayNames?: Record<string, string> | null
): InvoicePrintLineItem[] {
  return items.map((it) => ({
    description: lineItemDisplayName(it, lineDisplayNames),
    qty: it.quantity,
    rate: it.unitPrice,
    amount: it.lineTotal
  }));
}

export function customerToBillTo(
  customer: Customer | undefined,
  customerId: string | null
): InvoiceBillTo | null {
  if (!customerId) return null;
  if (!customer) {
    return { name: customerId, lines: [] };
  }
  const lines: string[] = [];
  if (customer.phone) lines.push(customer.phone);
  if (customer.email) lines.push(customer.email);
  lines.push(...formatAddressToLines(customer.address));
  return { name: customer.name, lines };
}

/** Prefer `Order.storeId`, else `Invoice.storeId` (Receipt has no storeId in schema). */
export function resolvePrintStoreId(
  order: Order | null,
  invoice: Invoice | null | undefined
): string | undefined {
  return order?.storeId ?? invoice?.storeId ?? undefined;
}

export function issuerFromCompanyAndStore(
  companyName: string | null,
  store: PrintableStore | undefined
): {
  businessName: string;
  addressLines: string[];
  phone?: string;
  email?: string;
} {
  const name = companyName?.trim() || store?.name?.trim() || "Store";
  const lines: string[] = [];
  if (
    companyName?.trim() &&
    store?.name?.trim() &&
    store.name !== companyName
  ) {
    lines.push(store.name);
  }
  lines.push(...formatAddressToLines(store?.address ?? null));
  if (!lines.length && store?.name?.trim()) lines.push(store.name);
  return {
    businessName: name,
    addressLines: lines,
    phone: store?.contactPhone ?? undefined,
    email: store?.contactEmail ?? undefined
  };
}

/** Print: invoice date with clock — full ISO issueDate, else calendar issue + time from createdAt. */
function invoicePrintDateTimeDisplay(invoice: Invoice): string {
  const issue = invoice.issueDate?.trim() ?? "";
  if (issue.includes("T")) {
    return formatDateTime(issue);
  }
  const clockSource = invoice.createdAt?.trim() ?? issue;
  const d = new Date(clockSource);
  if (Number.isNaN(d.getTime())) {
    return formatDate(issue || clockSource);
  }
  const timeStr = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  return `${formatDate(issue || clockSource)}, ${timeStr}`;
}

function paymentMethodLabel(method: string): string {
  if (method in PaymentMethodLabels) {
    return PaymentMethodLabels[method as PaymentMethod];
  }
  return method;
}

export function buildReceiptPrintLayoutProps(
  receipt: Receipt,
  invoice: Invoice | null | undefined,
  order: Order | null,
  companyName: string | null,
  store: PrintableStore | undefined,
  /** Cashier line on receipt (e.g. current user `firstName`). */
  cashierDisplay: string,
  /** From `usePrintOrderLineDisplayNames` (order `detail` only); unused when `detail` is empty. */
  lineDisplayNames: Record<string, string> | null | undefined,
  t: (key: string, fallback: string) => string
): ReceiptPrintLayoutProps {
  const inv =
    invoice && invoice.invoiceId === receipt.invoiceId ? invoice : null;
  const issuer = issuerFromCompanyAndStore(companyName, store);
  const lineItems = order
    ? orderItemsToReceiptLines(order.items, lineDisplayNames)
    : [];
  const subtotal = inv?.subtotalAmount ?? order?.subtotalAmount ?? 0;
  const discountAmount = inv?.discountAmount ?? order?.discountAmount ?? 0;
  const taxAmount = inv?.taxAmount ?? order?.taxAmount ?? 0;
  const total = inv?.totalAmount ?? order?.totalAmount ?? receipt.amount;

  return {
    businessName: issuer.businessName,
    addressLines: issuer.addressLines,
    phone: issuer.phone,
    documentTitle: t("receipt-print-document-title", "Sales receipt"),
    // Use createdAt (instant from OMS LocalDateTime), not paymentDate (calendar LocalDate → midnight UTC quirks in JS).
    dateTimeDisplay: formatDateTime(receipt.createdAt),
    receiptNumber: receipt.receiptId,
    receiptNumberLabel: t("receipt-print-number-label", "Receipt #"),
    cashierDisplay,
    lineItems,
    subtotal,
    discountLabel: t("discount", "Discount"),
    discountAmount,
    taxLabel: t("receipt-print-tax", "Tax"),
    taxAmount,
    total,
    thisPaymentAmount: receipt.amount,
    thisPaymentLabel: t(
      "receipt-print-paid-this-receipt",
      "Paid on this receipt"
    ),
    invoiceSummaryTitle: t("receipt-print-invoice-summary", "Invoice summary"),
    receiptSectionTitle: t("receipt-print-receipt-summary", "Receipt summary"),
    paymentMethodLabel: t("receipt-detail-payment-method", "Payment method"),
    paymentMethodDisplay: paymentMethodLabel(receipt.paymentMethod),
    thankYouMessage: t(
      "receipt-print-thank-you",
      "Thank you for your business."
    ),
    policyMessage: t(
      "receipt-print-policy",
      "Please retain this receipt for your records."
    )
  };
}

export function buildInvoicePrintLayoutProps(
  invoice: Invoice,
  order: Order | null,
  companyName: string | null,
  store: PrintableStore | undefined,
  customer: Customer | undefined,
  lineDisplayNames: Record<string, string> | null | undefined,
  t: (key: string, fallback: string) => string
): InvoicePrintLayoutProps {
  const issuer = issuerFromCompanyAndStore(companyName, store);
  const lineItems = order
    ? orderItemsToInvoiceLines(order.items, lineDisplayNames)
    : [];

  return {
    businessName: issuer.businessName,
    addressLines: issuer.addressLines,
    email: issuer.email,
    phone: issuer.phone,
    invoiceNumber: invoice.invoiceNumber,
    billTo: customerToBillTo(customer, invoice.customerId),
    invoiceDateDisplay: invoicePrintDateTimeDisplay(invoice),
    lineItems,
    subtotal: invoice.subtotalAmount,
    discountLabel: t("discount", "Discount"),
    discountAmount: invoice.discountAmount ?? 0,
    taxLabel: t("invoice-print-tax", "Tax"),
    taxAmount: invoice.taxAmount,
    totalDue: invoice.totalAmount,
    thankYouMessage: t(
      "invoice-print-thank-you",
      "We appreciate your business."
    )
  };
}
